import React from "react";
import { Helmet } from "react-helmet-async";
import { useAppContext } from "../../Context/AppContext/AppContext";

interface PageMetadataProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
}

const PageMetadata: React.FC<PageMetadataProps> = ({ 
  title, 
  description, 
  image,
  url
}) => {
  const { organization } = useAppContext();
  
  // Calculate meta image with fallback
  const metaImage = image || 
    (organization && organization?.logo
      ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${organization?._id}/${organization?.logo}`
      : `${window.location.origin}/images/qualifyme_light.png`);
  
  const metaUrl = url || window.location.href;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      
      {/* Open Graph Meta Tags - using key to force replacement */}
      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:title" property="og:title" content={title} />
      <meta key="og:description" property="og:description" content={description} />
      <meta key="og:url" property="og:url" content={metaUrl} />
      <meta key="og:site_name" property="og:site_name" content="QualifyMe" />
      <meta key="og:locale" property="og:locale" content="en_US" />
      <meta key="og:image" property="og:image" content={metaImage} />
      <meta key="og:image:width" property="og:image:width" content="1200" />
      <meta key="og:image:height" property="og:image:height" content="630" />
      <meta key="og:image:alt" property="og:image:alt" content={title} />
      <meta key="og:image:type" property="og:image:type" content="image/png" />
      
      {/* Twitter Card Meta Tags - using key to force replacement */}
      <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
      <meta key="twitter:title" name="twitter:title" content={title} />
      <meta key="twitter:description" name="twitter:description" content={description} />
      <meta key="twitter:site" name="twitter:site" content="@QualifyMe" />
      <meta key="twitter:creator" name="twitter:creator" content="@QualifyMe" />
      <meta key="twitter:image" name="twitter:image" content={metaImage} />
      <meta key="twitter:image:alt" name="twitter:image:alt" content={title} />
      
      {/* Additional Meta Tags for better sharing */}
      <meta key="robots" name="robots" content="index, follow" />
      <meta key="author" name="author" content="QualifyMe" />
      
      {/* Canonical URL */}
      <link key="canonical" rel="canonical" href={metaUrl} />
    </Helmet>
  );
};

export default PageMetadata;
